import type {
  Profile,
  RunningInstance,
  HealthStatus,
  CreateProfileInput,
} from '../types/profile'

const STORAGE_KEY = 'fb_api_base'

function resolveApiBase(): string {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) return stored.replace(/\/$/, '')
  // 开发环境走 vite 代理；生产环境必须用户在设置里填 Tunnel URL
  return ''
}

function getAuthToken(): string {
  return localStorage.getItem('fb_api_token') ?? ''
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const base = resolveApiBase()
  if (!base) {
    throw new Error('未配置后端地址，请先在「设置」里填入 Cloudflare Tunnel URL')
  }
  const token = getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${base}${path}`, { ...init, headers })
  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text().catch(() => res.statusText)
    }
    throw new Error(
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`,
    )
  }
  return res.json() as Promise<T>
}

export const api = {
  setApiBase(url: string) {
    localStorage.setItem(STORAGE_KEY, url.replace(/\/$/, ''))
  },
  getApiBase(): string {
    return resolveApiBase()
  },
  setToken(token: string) {
    localStorage.setItem('fb_api_token', token)
  },
  getToken(): string {
    return getAuthToken()
  },

  health(): Promise<HealthStatus> {
    return request('/api/v1/health')
  },

  listProfiles(): Promise<Profile[]> {
    return request('/api/v1/profiles')
  },

  getProfile(id: string): Promise<Profile> {
    return request(`/api/v1/profiles/${encodeURIComponent(id)}`)
  },

  createProfile(input: CreateProfileInput): Promise<Profile> {
    return request('/api/v1/profiles', {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  updateProfile(id: string, patch: Partial<Profile>): Promise<Profile> {
    return request(`/api/v1/profiles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },

  deleteProfile(id: string): Promise<{ ok: true }> {
    return request(`/api/v1/profiles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },

  launchProfile(
    id: string,
    options: { headless?: boolean } = {},
  ): Promise<RunningInstance> {
    return request(`/api/v1/profiles/${encodeURIComponent(id)}/launch`, {
      method: 'POST',
      body: JSON.stringify(options),
    })
  },

  stopProfile(id: string): Promise<{ ok: true }> {
    return request(`/api/v1/profiles/${encodeURIComponent(id)}/stop`, {
      method: 'POST',
    })
  },

  listRunning(): Promise<RunningInstance[]> {
    return request('/api/v1/running')
  },
}